import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/lib/api";
import { invalidateScope, useScopedKey } from "@/lib/queryClient";
import {
  isCurrentDraftContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
import { ownedRequest } from "@/features/coach/assist/useAssistContext";
import type {
  ApproveConfirmedCoachingDraftRequest,
  CoachingPublicationSelection,
  CreateCoachingConfirmationRequest,
  DiscardCoachingCaptureRequest,
  GenerateConfirmedCoachingDraftRequest,
} from "@/lib/types";
export function useDraftInbox(owner: DraftContext, captureId: string | null, visible = true) {
  const scope = { teamId: owner.teamId, seasonId: owner.seasonId };
  const client = useQueryClient();
  const identity = {
    actor: owner.actorId,
    club: owner.clubId,
    generation: owner.generation,
  };
  const inboxKey = useScopedKey("coach-draft-inbox", identity);
  const inbox = useQuery({
    queryKey: inboxKey,
    queryFn: ({ signal }) =>
      ownedRequest(owner, () => api.getCoachingDraftInbox(scope, signal)),
    retry: false,
    enabled: visible,
    refetchInterval: (q) => (!visible || q.state.error ? false : 2000),
    refetchOnWindowFocus: visible,
  });
  const item = inbox.data?.items.find((i) => i.capture_id === captureId);
  const sourceKey = useScopedKey("coach-draft-source", {
    ...identity,
    captureId,
    revision: item?.revision,
    sourceRevision: item?.source_revision,
  });
  const source = useQuery({
    queryKey: sourceKey,
    queryFn: ({ signal }) =>
      ownedRequest(owner, () =>
        api.getCoachingCapture(captureId!, scope, signal),
      ),
    enabled: visible && !!item?.source_revision,
    refetchOnWindowFocus: visible,
    retry: false,
    staleTime: 0,
  });
  const confirmationKey = useScopedKey("coach-draft-confirmation", {
    ...identity,
    id: item?.confirmation?.id,
    revision: item?.confirmation?.revision,
  });
  const draftKey = useScopedKey("coach-draft-proposal", {
    ...identity,
    id: item?.draft?.id,
    revision: item?.draft?.revision,
  });
  const confirmation = useQuery({
    queryKey: confirmationKey,
    queryFn: ({ signal }) =>
      ownedRequest(owner, () =>
        api.getCoachingConfirmation(item!.confirmation!.id, scope, signal),
      ),
    enabled: visible && !!item?.confirmation,
    refetchOnWindowFocus: visible,
    retry: false,
    staleTime: 0,
  });
  const draft = useQuery({
    queryKey: draftKey,
    queryFn: ({ signal }) =>
      ownedRequest(owner, () =>
        api.getCoachingApplicationDraft(item!.draft!.id, scope, signal),
      ),
    enabled: visible && !!item?.draft,
    refetchOnWindowFocus: visible,
    retry: false,
    staleTime: 0,
  });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(
    () => () => {
      for (const queryKey of [sourceKey, confirmationKey, draftKey]) {
        void client.cancelQueries({ queryKey, exact: true });
        client.removeQueries({ queryKey, exact: true });
      }
    },
    [
      client,
      JSON.stringify(sourceKey),
      JSON.stringify(confirmationKey),
      JSON.stringify(draftKey),
    ],
  );
  const mutation = useMutation({
    meta: { composerContext: owner },
    retry: false,
    mutationFn: (operation: () => Promise<unknown>) =>
      ownedRequest(owner, operation),
    onSuccess: () => {
      if (alive.current && isCurrentDraftContext(owner)) {
        void inbox.refetch();
        invalidateScope();
      }
    },
    onError: () => {
      // A committed write can lose its response and leave discovery. Refresh
      // this read without dispatching a new domain operation.
      if (alive.current && isCurrentDraftContext(owner)) void inbox.refetch();
    },
  });
  return {
    inbox,
    item,
    source,
    confirmation,
    draft,
    mutation,
    preview: (id: string, selection: CoachingPublicationSelection) =>
      ownedRequest(owner, () =>
        api.previewCoachingPublication(id, selection, scope),
      ),
    confirm: (id: string, body: CreateCoachingConfirmationRequest) =>
      api.confirmCoachingPublication(id, body, scope),
    generate: (id: string, body: GenerateConfirmedCoachingDraftRequest) =>
      api.generateConfirmedCoachingDraft(id, body, scope),
    approve: (id: string, body: ApproveConfirmedCoachingDraftRequest) =>
      api.approveCoachingDraft(id, body, scope),
    discard: (id: string, body: DiscardCoachingCaptureRequest) =>
      api.discardCoachingCapture(id, body, scope),
  };
}
